import {
  CsvDownloadService,
  IConfiguredDataDefinitionDto,
  IDataSummaryDto,
  IInputMetricDto,
  IUnitsOfMeasureDto,
  OutputChannelTypeId,
  TrendData,
  TrendDataConverter,
  TrendDataDefinition,
  TrendDataDefinitionConverter
} from '@/apps/timeSeriesViewer';
import {
  AggregateMetricDto,
  CalculatedMetricDto,
  ConfiguredMetricDto,
  DataSummaryDto,
  IMetricDto,
  RawMetricDto,
  SeriesDataCollectionRequestDto,
  TrendsMetricsApiClient,
  UnitsOfMeasureDto
} from '@/repositories';
import { IProgressStatus, ProgressStatus } from './progressStatus';
import { ResourcePumps } from './resourcePumps';
import { ITrendDataUpdater, TrendDataUpdater } from './trendDataUpdater';
import { CsvResourcePumps } from '@/apps/timeSeriesViewer/repos/api/csvResourcePumps';

function abortableFetch(abort: AbortWrapper) {
  const windowFetch = window.fetch;

  return (input: RequestInfo, init?: RequestInit) => {
    return windowFetch(input, { ...init, signal: abort.signal });
  };
}

class AbortWrapper {
  private _abortController: AbortController = new AbortController();

  get signal() {
    return this._abortController.signal;
  }

  reset() {
    if (this._abortController) this._abortController.abort();
    this._abortController = new AbortController();
  }
}

export class TrendsMetricsApi {
  private _abort: AbortWrapper = new AbortWrapper();
  private _fetch = abortableFetch(this._abort);

  constructor(
    private _trendDataDefinitionConverter: TrendDataDefinitionConverter,
    private _trendDataConverter: TrendDataConverter
  ) {}

  async fillData(
    dataDefinition: TrendDataDefinition,
    purpose: OutputChannelTypeId,
    trendData: KnockoutObservable<TrendData | null>,
    progress: KnockoutObservable<string>,
    isLoading: KnockoutObservable<boolean>,
    csvDownloadService: CsvDownloadService | null = null
  ): Promise<void> {
    progress('Requesting data');

    const dto = this._trendDataDefinitionConverter.toDto(dataDefinition);

    const request = new SeriesDataCollectionRequestDto({
      start: dto.start,
      end: dto.end,
      pointInTimeContext: dto.pointInTimeContext,
      configuredDataDefinitions: dto.configuredDataDefinitions.map((x) => ConfiguredMetricDto.fromJS(x)),
      requestId: dto.requestId,
      utcOffsetInMinutes: dto.utcOffsetInMinutes
    });

    const progressStatusCreator = (totalRequests: number) => new ProgressStatus(totalRequests, progress, isLoading);
    const trendDataUpdaterCreator = (configuredMetrics: { configuredMetric: IConfiguredDataDefinitionDto; isForecast: boolean }[]) =>
      new TrendDataUpdater(trendData, configuredMetrics, this._trendDataConverter, dataDefinition);
    await this.handleRequest(request, purpose, trendDataUpdaterCreator, progressStatusCreator, csvDownloadService);
  }

  public async handleRequest(
    request: SeriesDataCollectionRequestDto,
    purpose: OutputChannelTypeId,
    trendDataUpdaterCreator: (configuredMetrics: { configuredMetric: IConfiguredDataDefinitionDto; isForecast: boolean }[]) => ITrendDataUpdater,
    progressStatusCreator: (totalRequests: number) => IProgressStatus,
    csvDownloadService: CsvDownloadService | null = null
  ): Promise<void> {
    try {
      // abort any previous requests
      this._abort.reset();

      const api = new TrendsMetricsApiClient('', { fetch: this._fetch });

      const configuredMetrics: { configuredMetric: IConfiguredDataDefinitionDto; isForecast: boolean }[] = [];
      const availableRequests: { configuredMetricId: string; url: string }[] = [];
      const pendingRequests: { configuredMetricId: string; url: string }[] = [];
      const pendingRealtimeRequests: { configuredMetricId: string; url: string }[] = [];

      let responses = await api.trendsQuery(purpose, request);

      let pendingRealtimeRetries = 0;
      while (purpose == 'FILE' && pendingRealtimeRetries < 10 && responses.some((response) => response.pending.length > 0 || response.pendingRealtime.length > 0)) {
        const totalPending = responses.reduce((accumulator, r) => {
          return accumulator + r.pending.length;
        }, 0);
        const totalPendingRealtime = responses.reduce((accumulator, r) => {
          return accumulator + r.pendingRealtime.length;
        }, 0);
        const totalAvailable = responses.reduce((accumulator, r) => {
          return accumulator + r.available.length;
        }, 0);

        const requestStatus = progressStatusCreator(totalAvailable + totalPending + totalPendingRealtime);
        requestStatus.setCount(totalAvailable);

        if (totalPending == 0 && totalPendingRealtime > 0) pendingRealtimeRetries++;

        requestStatus.setMessage('Requesting');
        await delay(2000);
        responses = await api.trendsQuery(purpose, request);
      }

      responses.forEach((response) => {
        configuredMetrics.push({ configuredMetric: toIConfiguredDataDefinitionDto(response.configuredMetric), isForecast: response.isForecast });
        availableRequests.push(...response.available.map((x) => ({ configuredMetricId: response.configuredMetric.id, url: x })));
        pendingRequests.push(...response.pending.map((x) => ({ configuredMetricId: response.configuredMetric.id, url: x })));
        pendingRealtimeRequests.push(...response.pendingRealtime.map((x) => ({ configuredMetricId: response.configuredMetric.id, url: x })));
      });

      const trendDataUpdater = trendDataUpdaterCreator(configuredMetrics);
      const progressStatus = progressStatusCreator(availableRequests.length + pendingRequests.length + pendingRealtimeRequests.length);

      if (purpose == 'FILE') {
        const resourcePumps = new CsvResourcePumps(availableRequests, trendDataUpdater, progressStatus, this._abort.signal, this._fetch, csvDownloadService);
        await resourcePumps.run();
      } else {
        const resourcePumps = new ResourcePumps(api, availableRequests, pendingRequests, pendingRealtimeRequests, trendDataUpdater, progressStatus, this._abort.signal, this._fetch);
        await resourcePumps.run();
      }
    } catch (error) {
      if (error instanceof DOMException && error.code === DOMException.ABORT_ERR) {
        console.warn('User aborted request');
      } else {
        throw error;
      }
    }

    async function delay(ms: number): Promise<void> {
      await new Promise<void>((resolve) => setTimeout(() => resolve(), ms));
    }
  }
}

function toIConfiguredDataDefinitionDto(configuredMetric: ConfiguredMetricDto): IConfiguredDataDefinitionDto {
  return {
    id: configuredMetric.id,
    name: configuredMetric.name,
    calculationVariableName: configuredMetric.calculationVariableName,
    timeStep: configuredMetric.timeStep,
    aggregate: configuredMetric.aggregate,
    isIncludedInOutputChannels: configuredMetric.isIncludedInOutputChannels,
    inputDataDefinitionId: toIInputMetricDto(configuredMetric.inputDataDefinitionId),
    unitsOfMeasure: toIUnitsOfMeasureDto(configuredMetric.unitsOfMeasure),
    dataSummary: toIDataSummary(configuredMetric.dataSummary)
  };
}

function toIInputMetricDto(inputMetricDto: IMetricDto): IInputMetricDto {
  switch (inputMetricDto.type) {
    case 'Raw':
      return inputMetricDto as RawMetricDto as IInputMetricDto;
    case 'Aggregate':
      return inputMetricDto as AggregateMetricDto as IInputMetricDto;
    case 'Calculated':
      return inputMetricDto as CalculatedMetricDto as IInputMetricDto;
    default:
      throw 'unknown input metric type';
  }
}

function toIUnitsOfMeasureDto(unitsOfMeasure: UnitsOfMeasureDto): IUnitsOfMeasureDto {
  return {
    displayName: unitsOfMeasure.displayName,
    decimalPlaces: unitsOfMeasure.decimalPlaces,
    id: unitsOfMeasure.id,
    valueFormatString: unitsOfMeasure.valueFormatString
  };
}

function toIDataSummary(dataSummary: DataSummaryDto): IDataSummaryDto {
  return {
    period: dataSummary.period?.toString()
  };
}
